import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CmsPublishGate } from "@/components/cms/CmsPublishGate";
import { manualPayment } from "@/services/crmApi";
import { useAuth } from "@/context/AuthContext";

const EFFECT = {
  wallet_topup: "Credit this amount to the customer's wallet",
  razorpay_payments: "If this is a wallet top-up, also credit the customer's wallet",
  orders: "Also mark the order paid (sets amount_paid + payment_ref)",
  promise_requests: "Also activate Brugo Promise on the request and project",
  wallet_payments: null,
};

// ctx = { target, doc_id?, customer_id?, amount?, label }
export function ManualPaymentDialog({ ctx, onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({ status: "paid", txn: "", amount: ctx?.amount ?? "", customer: ctx?.customer_id || "", apply: true });
  const [gate, setGate] = useState(null);
  if (!ctx) return null;
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  const isAdmin = user?.role === "admin";
  const next = () => {
    if (f.status === "paid" && !f.txn.trim()) return toast.error("Enter the Razorpay payment / transaction ID");
    if (ctx.target === "wallet_topup" && (!f.customer.trim() || !(Number(f.amount) > 0))) return toast.error("Customer ID and amount are required");
    setGate({
      title: "Confirm manual payment update",
      run: async ({ password, otp_id_token, note }) => {
        await manualPayment({ target: ctx.target, doc_id: ctx.doc_id, customer_id: f.customer.trim(), status: f.status, manual_txn_id: f.txn.trim(),
          amount: Number(f.amount) || 0, note, apply_effect: !!EFFECT[ctx.target] && f.apply, password, otp_id_token });
        toast.success("Payment updated");
        onClose();
      },
    });
  };
  return (
    <>
      <Dialog open={!gate} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="manual-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{ctx.label || "Update payment"}</DialogTitle>
            <DialogDescription>For payments collected outside checkout (e.g. a Razorpay link sent on WhatsApp). Admins only · profile password + OTP.</DialogDescription>
          </DialogHeader>
          {!isAdmin && <p className="text-sm text-red-600" data-testid="manual-pay-admin-only">Only admins can record payments by hand.</p>}
          <div className="grid gap-3">
            {ctx.target === "wallet_topup" && <div className="space-y-1.5"><Label>Customer ID (uid) *</Label><Input value={f.customer} onChange={set("customer")} data-testid="manual-pay-customer" /></div>}
            <div className="space-y-1.5"><Label>Payment status *</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="manual-pay-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["paid", "pending", "failed", "refunded", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Razorpay payment / transaction ID {f.status === "paid" && "*"}</Label><Input value={f.txn} onChange={set("txn")} placeholder="pay_XXXXXXXXXXXX" className="font-mono" data-testid="manual-pay-txn" /></div>
            <div className="space-y-1.5"><Label>Amount received (₹)</Label><Input type="number" value={f.amount} onChange={set("amount")} data-testid="manual-pay-amount" /></div>
            {EFFECT[ctx.target] && f.status === "paid" && (
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.apply} onCheckedChange={set("apply")} data-testid="manual-pay-apply" />{EFFECT[ctx.target]}</label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} data-testid="manual-pay-cancel">Cancel</Button>
            <Button onClick={next} disabled={!isAdmin} data-testid="manual-pay-continue">Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CmsPublishGate gate={gate} onClose={() => setGate(null)} />
    </>
  );
}
